import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaPlus } from "react-icons/fa";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

import * as api from "../../api/api";
import {
    LocationCollection,
    LocationDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import urls from "../../urls.jsx";

export default function LocationCreatePage() {
    const { organizationId } = useParams();
    const navigate = useNavigate();

    const createLocationConfigSchema = () =>
        z.object({
            locationName: z.string().min(1, { message: "Please enter a name" }),
        });

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(createLocationConfigSchema()),
    });

    const onSubmit = async (formData) => {
        const name = formData.locationName;
        try {
            // 1) create the new location
            const newLocationId = await LocationCollection(organizationId).add({
                name,
                markerType: "ARGUS",
                timezone: "Etc/UTC",
                country: "Unknown",
                missionHandler: {
                    maxAutonomousFlights: 999,
                    version: "production",
                },
                mobileDock: {
                    takeoffHeight: 0.7,
                },
                visualization: {
                    showDiskSpaceOnDronePage: false,
                },
            });

            // 2) write back the generated id into the document
            await LocationDocument(organizationId, newLocationId).update({
                id: newLocationId,
            });

            // 3) Create empty markers, flyzones and areas
            await Promise.all([
                api.setMarkers(organizationId, newLocationId, []),
                api.setFlyzones(organizationId, newLocationId, []),
                api.setAreas(organizationId, newLocationId, {}),
            ]);

            // 4) navigate & mark saved
            navigate(urls.locationSettings(organizationId, newLocationId));
        } catch (err) {
            console.error("Failed to create & save new location:", err);
        }
    };

    return (
        <Layout>
            <title>{`New Location | MyCorvus`}</title>
            <Breadcrumbs path={breadcrumbPaths.locationCreate} />
            <Card>
                <Title
                    title={"Create Location"}
                    backButtonUrl={urls.locations(organizationId)}
                />
                <Card.Body className="p-4 py-0">
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <TextFormGroup
                            label="Location name"
                            name="locationName"
                            control={control}
                            errors={errors}
                        />
                        <Button
                            variant="success"
                            disabled={isSubmitting || !isDirty}
                            type="submit"
                            className="mb-3 mt-1"
                        >
                            <FaPlus className="mb-1 me-1" />
                            {isSubmitting ? "Creating..." : "Create"}
                        </Button>
                        <CancelButton
                            className="ms-2 mb-3 mt-1"
                            to={urls.locations(organizationId)}
                        />
                    </Form>
                </Card.Body>
            </Card>
        </Layout>
    );
}
